import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage';

import * as moment from 'moment-timezone';

import has from 'lodash/has';

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  /** The default expiry */
  private _expiry: number = 24; // hours

  /** The list of storage names to impose expiration */
  private _storageToExpire: string[] = [];

  /** Key to separate global storage */
  public globalKey: string = '';

  constructor(private storage: Storage) {}

  /**
   * Load data to the storage. Returns false if it does not exist.
   * @param key - The storage key
   */
  async load(key: string, global: boolean = true): Promise<any> {
    if (!global && this.globalKey) {
      key = key + '_' + this.globalKey;
    }

    const storedData = await this.storage.get(key);

    // use the device time
    const now = moment().valueOf();
    let retVal = false;

    if (storedData) {
      const parsedData = this._unwrapData(storedData);
      retVal = parsedData.data;

      // check validity
      if (this._storageToExpire.indexOf(key) > -1) {
        if (has(parsedData, 'expiry') && now >= parsedData.expiry) {
          retVal = false;
        }
      }
    }

    return retVal;
  }

  /**
   * Save data to the storage.
   * @param key - The storage key
   * @param data - The data to store
   * @param expiry - Optional. The expiration of stored data. Defaults to 24.
   */
  async save(key: string, data: any, expiry: number = 0, global: boolean = true): Promise<any> {
    if (!expiry) {
      expiry = this._expiry;
    }

    if (!global && this.globalKey) {
      key = key + '_' + this.globalKey;
    }

    // set the expiry - use the device time
    const expiredDate = moment().add(expiry, 'hours').valueOf();
    return this.storage.set(
      key,
      JSON.stringify({
        data: data,
        expiry: expiredDate,
      }),
    );
  }

  /**
   * Remove data from the storage
   * @param key - The storage key
   */
  async remove(key: string, global: boolean = true): Promise<any> {
    if (!global && this.globalKey) {
      key = key + '_' + this.globalKey;
    }

    this.storage.remove(key);
  }

  private _unwrapData(value: any): any {
    return typeof value === 'string' ? JSON.parse(value) : value;
  }
}
